import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const getApiUrl = () => {
  let url = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  // Se a URL não começar com http, assume que é um domínio e adiciona https
  if (url && !url.startsWith('http')) {
    url = `https://${url}`;
  }
  return url.replace(/\/$/, '');
};

const API_URL = getApiUrl();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem('wf_user');
      return u ? JSON.parse(u) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('wf_token') || null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  // [NOVO] Lista de todas as contas business do usuário
  const [userAccounts, setUserAccounts] = useState(() => {
    try {
      const a = localStorage.getItem('wf_accounts');
      return a ? JSON.parse(a) : [];
    } catch { return []; }
  });
  // [NOVO] Conta ativa selecionada (para visão business)
  const [activeAccountId, setActiveAccountIdState] = useState(
    () => localStorage.getItem('wf_active_account_id') || null
  );

  const fetchAndUpdateUser = useCallback(async (currentToken) => {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${currentToken}` },
    });
    // Não deslogar automaticamente por rate limit (429) ou erro de servidor (5xx)
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw new Error('Token inválido');
      } else {
        throw new Error('STATUS_' + res.status);
      }
    }
    const data = await res.json();
    if (data.token) {
      localStorage.setItem('wf_token', data.token);
      setToken(data.token);
    }
    if (data.user) {
      localStorage.setItem('wf_user', JSON.stringify(data.user));
      setUser(data.user);
    }
    return data;
  }, []);

  // [NOVO] Busca todas as contas business do usuário logado
  const fetchUserAccounts = useCallback(async (currentToken) => {
    try {
      const t = currentToken || localStorage.getItem('wf_token');
      if (!t) return;
      const res = await fetch(`${API_URL}/api/business/my-accounts`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) return;
      const accounts = await res.json();
      setUserAccounts(accounts || []);
      localStorage.setItem('wf_accounts', JSON.stringify(accounts || []));
    } catch (err) {
      console.error('fetchUserAccounts err:', err);
    }
  }, []);

  // [NOVO] Define a conta business ativa e persiste no localStorage
  const setActiveAccount = useCallback((accountId) => {
    setActiveAccountIdState(accountId);
    if (accountId) {
      localStorage.setItem('wf_active_account_id', accountId);
    } else {
      localStorage.removeItem('wf_active_account_id');
    }
  }, []);

  // Valida o token na inicialização chamando /api/auth/me
  useEffect(() => {
    const storedToken = localStorage.getItem('wf_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    fetchAndUpdateUser(storedToken)
      .then(() => fetchUserAccounts(storedToken))
      .catch((err) => {
        // Se for erro de status diferente de 401 ou 403 (ex: 429), apenas remove o loading, mas não limpa o token
        if (err.message === 'Token inválido') {
          localStorage.removeItem('wf_token');
          localStorage.removeItem('wf_user');
          localStorage.removeItem('wf_accounts');
          localStorage.removeItem('wf_active_account_id');
          localStorage.removeItem('wf_vision');
          setToken(null);
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, [fetchAndUpdateUser, fetchUserAccounts]);

  const refreshUser = useCallback(async () => {
    const currentToken = localStorage.getItem('wf_token');
    if (!currentToken) return;
    try {
      await fetchAndUpdateUser(currentToken);
      await fetchUserAccounts(currentToken);
    } catch (err) {
      console.error('refreshUser err:', err);
    }
  }, [fetchAndUpdateUser, fetchUserAccounts]);

  const login = useCallback(async (email, password) => {
    const res  = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || data.error || 'Erro ao fazer login');

    localStorage.setItem('wf_token', data.token);
    localStorage.setItem('wf_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    setSessionExpired(false);
    // [NOVO] Carrega contas business do usuário após login
    fetchUserAccounts(data.token);
    return data;
  }, [fetchUserAccounts]);

  const register = useCallback(async (name, email, password) => {
    const res  = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Erro ao cadastrar');

    if (!data.requireVerification && data.token) {
      localStorage.setItem('wf_token', data.token);
      localStorage.setItem('wf_user', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
    }
    setSessionExpired(false); // Garante que o estado de expiração seja limpo ao cadastrar
    return data;
  }, []);


  const logout = useCallback(() => {
    localStorage.removeItem('wf_token');
    localStorage.removeItem('wf_user');
    localStorage.removeItem('wf_accounts');
    localStorage.removeItem('wf_active_account_id');
    localStorage.removeItem('wf_vision');
    setToken(null);
    setUser(null);
    setUserAccounts([]);
    setActiveAccountIdState(null);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res  = await fetch(`${API_URL}/api/auth/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Erro ao trocar senha');
    return data;
  }, [token]);

  // Fetch autenticado com tratamento de 401 automático
  const authFetch = useCallback(async (url, options = {}) => {
    const headers = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    };

    // Só adiciona Content-Type json se não for FormData para não quebrar uploads
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    const res = await fetch(`${API_URL}${url}`, { ...options, headers });

    // Token expirado → desloga automaticamente (Apenas se não for erro de rate limit ou server)
    if (res.status === 401) {
      localStorage.removeItem('wf_token');
      localStorage.removeItem('wf_user');
      localStorage.removeItem('wf_accounts');
      localStorage.removeItem('wf_active_account_id');
      localStorage.removeItem('wf_vision');
      setToken(null);
      setUser(null);
      setSessionExpired(true);
    }
    return res;
  }, [token]);

  // Logout automático por inatividade (15 minutos)
  useEffect(() => {
    if (!user) return; 

    const INACTIVITY_TIMEOUT = 15 * 60 * 1000; 
    let timeoutId;
    let lastActivity = Date.now();
    let debounceTimer;

    const resetTimer = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        lastActivity = Date.now();
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          logout();
          setSessionExpired(true);
        }, INACTIVITY_TIMEOUT);
      }, 200); // 200ms debounce para eventos de alta frequência (mousemove)
    };

    const checkTimeoutOnFocus = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastActivity > INACTIVITY_TIMEOUT) {
          logout();
          setSessionExpired(true);
        } else {
          // Re-sincroniza o timer se ainda não expirou
          clearTimeout(timeoutId);
          const remaining = INACTIVITY_TIMEOUT - (now - lastActivity);
          timeoutId = setTimeout(() => {
            logout();
            setSessionExpired(true);
          }, remaining);
        }
      }
    };

    resetTimer(); 

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    events.forEach(event => document.addEventListener(event, resetTimer));
    document.addEventListener('visibilitychange', checkTimeoutOnFocus);

    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => document.removeEventListener(event, resetTimer));
      document.removeEventListener('visibilitychange', checkTimeoutOnFocus);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, sessionExpired, setSessionExpired,
      isAuthenticated: !!user,
      login, register, logout, changePassword, authFetch, refreshUser,
      // [NOVO] Suporte a múltiplas contas business
      userAccounts, fetchUserAccounts,
      activeAccountId, setActiveAccount,
      API_URL,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return context;
};

export default AuthContext;
